import {
  bidAgeLabel,
  bidHistoryLabel,
  describeBidFailure,
  describeMyPosition,
  explainPointlessBid,
  feedSaleStateFromAuctionStatus,
  fetchEngineLotHistory,
  fetchLotBidHistory,
  fetchHouseIdentity,
  fetchLotPhotos,
  fetchMyLotPosition,
  fetchMyLotStandings,
  historyEntryFromEvent,
  houseLabel,
  conditionLabel,
  isLotWatched,
  isOwnEntry,
  listingPriceLines,
  lotSoundFor,
  mergeBidEntries,
  ownSeqFromYou,
  parsePickupWindows,
  positionWithYou,
  resolvePickup,
  resolveStanding,
  setWatched,
  soundLotFromRow,
  toLiveLot,
  withLiveFigures,
  type LotBidHistoryEntry,
  type MyLotPosition,
  type ResolvedPickup,
} from '@auctisync/api-client';
import { HelpTooltip } from '@auctisync/ui';
import {
  bidIncrementTiles,
  formatCents,
  parseBidToCents,
  type BidderStanding,
} from '@auctisync/shared';
import { AUCTION_ROW_COLUMNS, type AuctionRow } from '../../lib/rowColumns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CcPreauthPanel } from '../../components/CcPreauthPanel';
import { Countdown } from '../../components/Countdown';
import { FollowButton } from '../../components/FollowButton';
import { LotAlertControls } from '../../components/LotAlertControls';
import { LotBadges } from '../../components/LotBadges';
import { LotFunFacts } from '../../components/LotFunFacts';
import { LotQA } from '../../components/LotQA';
import { SwipePhotoCarousel } from '../../components/SwipePhotoCarousel';
import { UrgencyBadge } from '../../components/UrgencyBadge';
import { LiveStatusBanner } from '../../components/bidder-web/LiveStatusBanner';
import { SaleAside } from '../../components/bidder-web/SaleAside';
import { SaleHeader } from '../../components/bidder-web/SaleHeader';
import { SoundControls } from '../../components/bidder-web/SoundControls';
import { useLiveSaleAside } from '../../components/bidder-web/useLiveSaleAside';
import { useBidderHouses } from '../../hooks/useBidderHouses';
import { useConnection } from '../../hooks/useConnection';
import { useFirstBid } from '../../hooks/useFirstBid';
import { useLiveClock } from '../../hooks/useLiveClock';
import { useLiveSource } from '../../hooks/useLiveSource';
import { useLotSubscription } from '../../hooks/useLotSubscription';
import { useFeedEvent, useSaleFeed } from '../../hooks/useSaleFeed';
import { useSession } from '../../hooks/useSession';
import { playLotSound } from '../../lib/bidSound';
import { SpecsTable } from '../../components/bidder-web/SpecsTable';
import { apiClient, supabase } from '../../lib/supabase';
import { currentAccessToken } from '../../lib/accessToken';
import { PUBLIC_LOT_COLUMNS, type PublicLotRow } from '../../lib/lotColumns';

// Public surface: anon is not granted the reserve value or bidder identity
// columns (migration 20260720130000), so this page never receives them.
type Lot = PublicLotRow;
type Auction = AuctionRow;
type Photo = { id: string; display_url: string; sort_order: number; caption: string | null };

/**
 * A bid-history row: from the database (`get_lot_bid_history`), or from the
 * engine's live `event` (which carries its sequence). Every row carries its
 * lot, so `mergeBidEntries` can tell the two copies of one bid apart from two
 * bids.
 */
type HistoryLine = LotBidHistoryEntry & { lotId: string; seq?: string; mineKnown?: boolean };

/** The database caps the lot history at 50 rows; the page holds the same. */
const HISTORY_LINES = 50;

/** Same holdings, figure for figure — how the Supabase book tells the bidder's own bid. */
function samePosition(a: MyLotPosition, b: MyLotPosition): boolean {
  return (
    a.hasBid === b.hasBid &&
    a.ceilingCents === b.ceilingCents &&
    a.highestBidCents === b.highestBidCents
  );
}

/**
 * Lot detail — the page the whole site exists to serve.
 *
 * REBUILT to the web design handoff (`web/web-lot.jsx`, 2026-09-02). The
 * bidding, approval, pre-auth, watchlist, photo, Q&A and alert logic is the
 * code that was already here; what changed is the shape it renders in.
 *
 * 🔴 THE BID PANEL IS STICKY AND EXPLICITLY PLACED. `.w-lot` puts the gallery
 * and the body in column one and spans the panel down column two, so a bidder
 * reading a condition report still has the price and the button on screen.
 * Source order alone would drop the panel below everything. Below 1100px the
 * grid flattens AND the panel stops being sticky — a sticky panel in a single
 * column pins itself over the content it was meant to sit beside.
 *
 * WHAT THE REDESIGN ADDED, not just re-styled:
 *  - Bid history. `fetchLotBidHistory` has existed in the shared client since
 *    the app was built; the website never rendered it. Every other bidder shows
 *    as "bidder" and no maximum is ever shown, which is the RPC's own contract.
 *  - A standing tag (winning / outbid), from `fetchMyLotStandings` +
 *    `resolveStanding` — the same pair the phone uses.
 *  - Four quick bid amounts from `bidIncrementTiles`, the SAME function behind
 *    the phone's bid sheet. It moved into `@auctisync/shared` for this; the
 *    alternative was a second increment ladder typed by hand on the money path.
 *  - A pickup-and-payment accordion, resolved by `resolvePickup`.
 *  - A rejection DIALOG rather than an inline red box. The web convention for
 *    an answer to a deliberate click is a centred dialog; the copy, the tone
 *    rules and the re-priced next minimum are identical to the app's sheet.
 *
 * 🔴 LIVE THROUGH THE ENGINE (2026-10-08). Jim: the engine owns everything
 * live. On a sale the engine runs this page opens a one-lot feed (view = this
 * lot): the price, bid count, clock, state, reserve and next minimum are the
 * ENGINE's (`toLiveLot` / `withLiveFigures` over the catalogue row); new bids
 * join the history from the engine's `event` messages; the bidder's own
 * standing and ceiling are the engine's `you`. A sale on the Supabase book
 * keeps Realtime. `useLiveSource` decides, from the bid path, so the price on
 * the page and the book the bid goes to cannot disagree.
 *
 * 🔴 THE 4-SECOND "OWN BID" GUESS IS GONE. A rival's bid inside four seconds
 * of pressing Bid was silenced, and the bidder's own chimed whenever its echo
 * was slow. On the engine, "own" is the engine's `you` (`lotSoundFor`); on
 * the Supabase book, whether the bidder's own holdings moved with the change.
 */
export function PublicLotDetail() {
  const { slug, lotNumber } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Every read keys on `userId` — WHO is viewing — never the session object,
  // which Supabase replaces on every return to the tab: keyed on it, the page
  // went back to "Loading lot…" mid-sale each time a bidder switched tabs.
  const { session, userId } = useSession();
  const { state: connection } = useConnection();
  // Publishes the house this lot belongs to, so the shell can name it even for
  // a visitor with no account — see `discoverHouse`.
  const { active: activeHouse, discoverHouse } = useBidderHouses();

  const [auction, setAuction] = useState<Auction | null>(null);
  const [lot, setLot] = useState<Lot | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [nextMinCents, setNextMinCents] = useState<number | null>(null);
  // What THIS bidder holds on the lot. Their ceiling is secret from rivals, not
  // from them — hiding it is how a bidder re-bids under their own maximum and
  // sees nothing happen. Same rule, same wording, as the native app.
  const [myPosition, setMyPosition] = useState<MyLotPosition>({
    hasBid: false,
    ceilingCents: null,
    highestBidCents: null,
  });
  const [standing, setStanding] = useState<BidderStanding | undefined>(undefined);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [bidAmount, setBidAmount] = useState<string>('');
  const [bidBusy, setBidBusy] = useState(false);
  // Carries the TONE as well as the sentence. "Your maximum is already in" is
  // not a failure, and the danger-red panel this used to render made a bid that
  // had worked look like one that had been declined (reported on the phone
  // 2026-08-05; the website had the identical bug).
  //
  // Only NOTICES land here now. A server refusal opens the dialog instead —
  // see `reject`.
  const [bidNotice, setBidNotice] = useState<string | null>(null);
  const [bidOk, setBidOk] = useState<string | null>(null);
  /** A refused bid, held open until the bidder dismisses or re-bids. */
  const [reject, setReject] = useState<{ text: string; amountCents: number } | null>(null);
  // The primer shows until a bid of theirs is ACCEPTED — see `useFirstBid`.
  const { showPrimer, markFirstBid } = useFirstBid();

  const [isWatched, setIsWatched] = useState(false);
  const [watchBusy, setWatchBusy] = useState(false);

  const [bidHistory, setBidHistory] = useState<HistoryLine[]>([]);
  /** Bumped once the page has (re)set the lot and its held history: the engine's read follows. */
  const [historyRead, setHistoryRead] = useState(0);
  const [pickup, setPickup] = useState<ResolvedPickup | null>(null);

  // Tracks the last lot state we handed to setLot — lets us detect a
  // realtime "new bid landed" event without reading React state from
  // inside a callback (which is racy under StrictMode).
  const lotRef = useRef<Lot | null>(null);
  /** The sequences of the bidder's own changes, from the engine's `you`. */
  const [ownSeqs, setOwnSeqs] = useState<ReadonlySet<string>>(new Set());
  /** The lot and viewer the held history and "you" marks belong to. */
  const heldFor = useRef<{ lotId: string; userId: string | null } | null>(null);
  /** What the bidder held, for the Supabase book's own-bid test (see `onRealtimeRow`). */
  const positionRef = useRef<MyLotPosition>({
    hasBid: false,
    ceilingCents: null,
    highestBidCents: null,
  });
  /** Their holdings just before a bid of theirs was sent, until its change is seen. */
  const pendingOwnRef = useRef<MyLotPosition | null>(null);

  const [approvalStatus, setApprovalStatus] = useState<
    'none' | 'pending' | 'approved' | 'rejected' | 'revoked'
  >('none');
  const [approvalDecisionNote, setApprovalDecisionNote] = useState<string | null>(null);
  const [approvalRequestNote, setApprovalRequestNote] = useState<string>('');
  const [approvalBusy, setApprovalBusy] = useState(false);
  const [approvalMsg, setApprovalMsg] = useState<string | null>(null);

  const [hasActivePreauth, setHasActivePreauth] = useState(false);
  // Bump to force CcPreauthPanel to re-check when the bidder just saved a card.
  const [preauthRefreshKey, setPreauthRefreshKey] = useState(0);

  const [specs, setSpecs] = useState<Record<string, unknown> | null>(null);

  /**
   * `?bid=1` — the Bid shortcut from Focused Watch in the live room.
   *
   * 🔴 IT FOCUSES. IT DOES NOT BID. No submit, no pre-filled amount: the
   * existing next-minimum placeholder is all the hint there is. The param is
   * read through the router rather than `window.location` so it survives a
   * client-side navigation, and it never reaches a bid payload.
   *
   * `preventScroll` because a raw focus on a page this long jumps somewhere
   * unpredictable; the panel is scrolled into view deliberately instead.
   *
   * Guests have no field to focus — the panel says "Sign in to bid" — so the
   * effect simply finds nothing and does nothing. The sign-in link is NOT
   * focused: moving a guest's caret onto a link they did not ask for is worse
   * than leaving the page where it is.
   */
  const [searchParams] = useSearchParams();
  const bidIntent = searchParams.get('bid') === '1';
  const bidField = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!bidIntent || bidField.current === null) return;
    bidField.current.focus({ preventScroll: true });
    bidField.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [bidIntent, lot]);

  /**
   * Re-reads what this bidder holds. Separate from `refreshLot` on purpose: it
   * changes on a different trigger (this bidder bidding) and reads a different
   * table, and a failure here must cost a label, not the lot.
   */
  const refreshMyPosition = useCallback(async () => {
    const lotId = lotRef.current?.id;
    if (!lotId || userId === null) return;
    try {
      const position = await fetchMyLotPosition(supabase, lotId);
      positionRef.current = position;
      setMyPosition(position);
    } catch {
      // A missing position line is not worth breaking the bidding page over.
    }
  }, [userId]);

  useEffect(() => {
    void refreshMyPosition();
  }, [refreshMyPosition, lot?.id]);

  /**
   * The anonymized history. Read through the shared client so the website and
   * the phone render the same rows from the same SECURITY DEFINER projection —
   * a hand-written query here is how the two would come to disagree about what
   * a bidder is allowed to see.
   */
  const refreshHistory = useCallback(async () => {
    const lotId = lotRef.current?.id;
    if (!lotId) return;
    const res = await fetchLotBidHistory(supabase, lotId);
    if (lotRef.current?.id !== lotId) return;
    // MERGED, not replaced: on the engine the database copy can trail the
    // live rows (the write-behind), and replacing would drop the newest bids.
    setBidHistory((prev) =>
      mergeBidEntries([res.entries.map((e) => ({ ...e, lotId })), prev], HISTORY_LINES),
    );
  }, []);

  const refreshLot = useCallback(async () => {
    if (!auction) return;
    if (!lotNumber) return;
    const { data } = await supabase
      .from('lots')
      .select(PUBLIC_LOT_COLUMNS)
      .eq('auction_id', auction.id)
      .eq('lot_number', parseInt(lotNumber, 10))
      .maybeSingle();
    if (data) {
      lotRef.current = data;
      setLot(data);
      await computeNextMin(data, auction);
    }
  }, [auction, lotNumber]);

  // ── live: where updates come from, and the one-lot feed ──
  const { source } = useLiveSource(lot?.id ?? null);
  const lotIdForFeed = lot?.id ?? null;
  const live = useSaleFeed(source, {
    view: lotIdForFeed === null ? [] : [lotIdForFeed],
    // The sale-wide ticker feeds the "Activity" panel beside the sale.
    ticker: true,
    initialSaleState: feedSaleStateFromAuctionStatus(auction?.status),
  });
  const feed = live.feed;

  /* The sale-wide standing and activity for the panels beside the sale, LIVE
     (`useLiveSaleAside`, the Live Room's rule). Named apart from `standing`
     below, which is THIS lot's — two different questions that would otherwise
     share one word. */
  const { standing: saleStanding, events: saleEvents } = useLiveSaleAside({
    auctionId: auction?.id ?? null,
    feed,
    sourceKind: source?.kind ?? null,
    feedStatus: live.status,
    offline: connection === 'offline',
  });
  const nowServerMs = useLiveClock(feed);

  /**
   * A sale on the SUPABASE BOOK (the parachute): Realtime keeps the row live.
   * The sound is the shared rule over the rows (`soundLotFromRow`). "Was it my
   * bid?" cannot come from the engine here, so it comes from the bidder's own
   * record: if what they hold on this lot moved with the change, the change
   * was theirs — decided by the server, never by a clock.
   */
  const handleLiveLotUpdate = useCallback(
    (incoming: Lot) => {
      const prev = lotRef.current;
      if (!prev || prev.id !== incoming.id) return;
      const bidAdded = (incoming.bid_count ?? 0) > (prev.bid_count ?? 0);
      const merged = { ...prev, ...incoming };
      lotRef.current = merged;
      setLot(merged);
      setBidNotice(null);
      const sound = lotSoundFor({
        prev: soundLotFromRow(prev),
        next: soundLotFromRow(merged),
        onScreen: true,
        you: undefined,
      });
      if (sound === 'bid' && userId !== null) {
        const before = pendingOwnRef.current ?? positionRef.current;
        void (async () => {
          let after: MyLotPosition;
          try {
            after = await fetchMyLotPosition(supabase, incoming.id);
          } catch {
            // Unknown whose bid it was: stay silent rather than risk chiming
            // the bidder for their own (the rule is "never for your own").
            return;
          }
          if (lotRef.current?.id !== incoming.id) return;
          positionRef.current = after;
          setMyPosition(after);
          if (!samePosition(before, after)) {
            pendingOwnRef.current = null;
            return;
          }
          playLotSound('bid');
        })();
      } else {
        playLotSound(sound);
      }
      if (bidAdded) {
        void refreshHistory();
      }
      if (auction) {
        void computeNextMin(merged, auction);
      }
    },
    [auction, refreshHistory, userId],
  );

  const { isLive: realtimeLive } = useLotSubscription({
    target: lot ? { kind: 'lot', lotId: lot.id } : { kind: 'lot', lotId: '' },
    onUpdate: handleLiveLotUpdate,
    enabled: Boolean(lot) && source?.kind === 'supabase',
  });
  const isLive = source?.kind === 'engine' ? live.status?.status === 'live' : realtimeLive;

  /**
   * ON THE ENGINE: the shared sound rule for this lot (the page IS the lot, so
   * it is on screen) — a bid that was not the bidder's own (the engine's
   * `you` says), or the gavel. The figures need no handler: the page reads
   * the feed on every render.
   */
  useFeedEvent(feed, 'lots', (changes) => {
    if (feed === null) return;
    for (const { lot: next, prev } of changes) {
      if (next.lot_id !== lotRef.current?.id) continue;
      playLotSound(lotSoundFor({ prev, next, onScreen: true, you: feed.you(next.lot_id) }));
      setBidNotice(null);
    }
  });

  /** A new bid in the engine's trail joins the history at once. */
  useFeedEvent(feed, 'event', (event) => {
    const lotId = lotRef.current?.id;
    if (lotId === undefined || event.lot_id !== lotId) return;
    const entry = historyEntryFromEvent(event);
    if (entry === null) return;
    setBidHistory((prev) => mergeBidEntries([[entry], prev], HISTORY_LINES));
  });

  /** The bidder's own standing moved: remember the change as theirs. */
  useFeedEvent(feed, 'you', (you) => {
    const seq = ownSeqFromYou(you);
    if (seq !== null) setOwnSeqs((prev) => (prev.has(seq) ? prev : new Set(prev).add(seq)));
  });

  /**
   * ON THE ENGINE, an OPEN lot's history is the engine's (2026-10-11): the
   * database receives a lot's bids only when it closes, so its read answers an
   * open lot with nothing and a bidder arriving mid-sale saw an empty history.
   * `fetchEngineLotHistory` — the app's too — reads the engine's trail, with
   * the house's privacy applied and the bidder's own marked (`mine`). MERGED
   * with the database's rows (a closed lot's, the Supabase book's) and the
   * live events, each bid once. A sale the engine no longer holds is the
   * database's alone.
   */
  const engineRoute = source?.kind === 'engine' ? source : null;
  const refreshEngineHistory = useCallback(async () => {
    const lotId = lotRef.current?.id;
    if (engineRoute === null || !lotId) return;
    const res = await fetchEngineLotHistory(engineRoute, lotId, { getToken: currentAccessToken });
    if (lotRef.current?.id !== lotId || res.error !== null || res.notOnEngine) return;
    setBidHistory((prev) => mergeBidEntries([res.entries, prev], HISTORY_LINES));
  }, [engineRoute]);
  useEffect(() => {
    if (historyRead > 0) void refreshEngineHistory();
  }, [refreshEngineHistory, historyRead]);

  // Back from a drop: read the history the page may have missed.
  const wasDown = useRef(false);
  const feedState = live.status?.status;
  useEffect(() => {
    if (feedState === 'reconnecting' || feedState === 'paused') wasDown.current = true;
    if (feedState === 'live' && wasDown.current) {
      wasDown.current = false;
      void refreshHistory();
      void refreshEngineHistory();
    }
  }, [feedState, refreshHistory, refreshEngineHistory]);

  async function computeNextMin(lotRow: Lot, auctionRow: Auction) {
    const scaleId = lotRow.bid_increment_scale_id ?? auctionRow.default_bid_increment_scale_id;
    if (!scaleId) {
      setNextMinCents((lotRow.current_bid_cents ?? lotRow.starting_bid_cents) + 100);
      return;
    }
    const current = lotRow.current_bid_cents;
    if (current === null) {
      setNextMinCents(lotRow.starting_bid_cents);
      return;
    }
    const { data: incRaw } = await supabase.rpc('bid_increment_for_amount', {
      p_scale_id: scaleId,
      p_current_cents: current,
    });
    const inc = typeof incRaw === 'number' ? incRaw : Number(incRaw ?? 100);
    setNextMinCents(current + inc);
  }

  useEffect(() => {
    if (!slug || !lotNumber) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data: auctionData, error: aErr } = await supabase
        .from('auctions')
        .select(AUCTION_ROW_COLUMNS)
        .eq('slug', slug)
        .eq('is_visible', true)
        .maybeSingle();
      if (cancelled) return;
      if (aErr || !auctionData) {
        setError(aErr?.message ?? 'Auction not found.');
        setLoading(false);
        return;
      }
      setAuction(auctionData);

      // WHOSE SALE IS THIS. `tenants` is unreadable to anon, so the public
      // identity comes through `get_house_identity`. Without it every sentence
      // that names the house fell back to "AuctiSync" — or, worse, to the SALE
      // title, producing "an account with [TEST] Always-On App Sale".
      void fetchHouseIdentity(supabase, auctionData.tenant_id).then((house) => {
        if (!cancelled) discoverHouse(house);
      });

      const { data: lotData, error: lErr } = await supabase
        .from('lots')
        .select(PUBLIC_LOT_COLUMNS)
        .eq('auction_id', auctionData.id)
        .eq('lot_number', parseInt(lotNumber, 10))
        .maybeSingle();
      if (cancelled) return;
      if (lErr || !lotData) {
        setError(lErr?.message ?? 'Lot not found.');
        setLoading(false);
        return;
      }
      // WHAT THE PAGE HOLDS, AND WHETHER IT STAYS. The lot's feed opens the
      // moment the lot is set below, while the history read further down is
      // still in flight; the engine can deliver bids, and the bidder's `you`,
      // in that gap. They stay when the page re-reads the same lot as the
      // session arrives (a guest signing in sees no less). A different lot or
      // a different viewer (signed out, or someone else signed in) starts
      // clean: the engine masks its rows per viewer, so the last viewer's are
      // not this one's. Decided HERE, before the feed can deliver, never after
      // the read, which is how a bid that landed in the gap used to vanish.
      // (A token renewing is the same viewer and re-reads nothing — `userId`.)
      const held = heldFor.current;
      const keepHeld =
        held !== null &&
        held.lotId === lotData.id &&
        (held.userId === null || held.userId === userId);
      heldFor.current = { lotId: lotData.id, userId };
      if (!keepHeld) {
        setBidHistory([]);
        setOwnSeqs(new Set());
      }
      lotRef.current = lotData;
      setLot(lotData);
      // The engine's history is read AFTER the reset above, never before it.
      setHistoryRead((n) => n + 1);
      await computeNextMin(lotData, auctionData);

      // Pickup and collection, resolved by the SAME precedence the invoice and
      // the phone's pickup screen use: a sale that states its own address
      // outranks the house's standing answer, field by field.
      setPickup(
        resolvePickup({
          auction: {
            auctionId: auctionData.id,
            location: auctionData.pickup_location,
            addressLine1: auctionData.pickup_address_line1,
            addressLine2: auctionData.pickup_address_line2,
            city: auctionData.pickup_city,
            region: auctionData.pickup_region,
            postalCode: auctionData.pickup_postal_code,
            instructions: auctionData.pickup_instructions,
            // Was hardcoded `[]` while nothing wrote `pickup_dates` — correct
            // then, a bug since the auction form's collection scheduler shipped
            // on 2026-09-18. The row is fetched with `select('*')`, so the
            // windows were already in hand and were being thrown away.
            windows: parsePickupWindows(auctionData.pickup_dates),
            timeZone: auctionData.timezone,
          },
        }),
      );

      // PHOTOS ARE NOT GATED ON `inventory_item_id`. They used to be, and a lot
      // with photos attached directly to it — no source item — therefore showed
      // none at all. The resolver handles both link paths and is the SAME one
      // the native app calls, so the two surfaces cannot show different pictures
      // for the same lot. See `memory/feedback_website_and_app_stay_identical.md`.
      const lotPhotos = await fetchLotPhotos(supabase, [lotData]);
      if (!cancelled) {
        setPhotos(
          (lotPhotos.get(lotData.id) ?? []).map((p, i) => ({
            id: `${lotData.id}:${String(i)}`,
            display_url: p.url,
            sort_order: p.sortOrder,
            caption: null,
          })),
        );
      }

      // Always ask. `get_lot_bid_history` enforces the sale's privacy flags
      // itself now (20260908130000): with the trail hidden it returns the
      // caller's own bids and nothing else, and with winning info hidden it
      // returns null amounts for everyone else's. Skipping the call here would
      // also hide the bidder's own bids, which are theirs to see.
      const history = await fetchLotBidHistory(supabase, lotData.id);
      if (!cancelled) {
        // MERGED with the engine's rows held above, not replacing them —
        // replacing dropped a bid the engine delivered while this read was in
        // flight (CI, 2026-10-09). The database's own rows come only from this
        // read: what it returns now is what this viewer may see.
        setBidHistory((prev) =>
          mergeBidEntries(
            [
              history.entries.map((e) => ({ ...e, lotId: lotData.id })),
              prev.filter((e) => e.seq !== undefined),
            ],
            HISTORY_LINES,
          ),
        );
      }

      if (lotData.inventory_item_id) {
        const { data: itemData } = await supabase
          .from('inventory_items')
          .select('custom_fields')
          .eq('id', lotData.inventory_item_id)
          .maybeSingle();
        if (!cancelled) {
          const cf = itemData?.custom_fields;
          if (cf && typeof cf === 'object' && !Array.isArray(cf)) {
            setSpecs(cf as Record<string, unknown>);
          } else {
            setSpecs(null);
          }
        }
      }

      // Per-user: watchlist + approval status + cc preauth (only if signed in)
      if (userId !== null) {
        const [watching, { data: approvalRow }, { data: preauthRow }] = await Promise.all([
          // Shared with the native app (P2.7) — one implementation of "is
          // this lot on my list", so the two surfaces cannot disagree.
          isLotWatched(supabase, userId, lotData.id),
          auctionData.requires_bidder_approval
            ? supabase
                .from('bidder_approvals')
                .select('status, decision_note')
                .eq('auction_id', auctionData.id)
                .eq('user_id', userId)
                .maybeSingle()
            : Promise.resolve({ data: null }),
          auctionData.requires_cc_preauth
            ? supabase
                .from('cc_preauths')
                .select('id')
                .eq('auction_id', auctionData.id)
                .eq('user_id', userId)
                .eq('status', 'authorized')
                .maybeSingle()
            : Promise.resolve({ data: null }),
        ]);
        if (cancelled) return;
        setIsWatched(watching);
        if (approvalRow) {
          setApprovalStatus(approvalRow.status as typeof approvalStatus);
          setApprovalDecisionNote(approvalRow.decision_note ?? null);
        }
        setHasActivePreauth(Boolean(preauthRow));
      }

      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, lotNumber, userId]);

  /**
   * Winning or outbid, from booleans the server resolves against `auth.uid()`.
   *
   * The RPC alone cannot express "outbid" — a bidder who was overtaken looks
   * identical to one who never bid — so `resolveStanding` combines it with
   * whether this viewer holds any bid. Exactly the pair the phone uses.
   */
  useEffect(() => {
    if (userId === null || !auction || !lot) {
      setStanding(undefined);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const flags = await fetchMyLotStandings(supabase, auction.id);
        if (cancelled) return;
        setStanding(resolveStanding(flags.get(lot.id), myPosition.hasBid));
      } catch {
        // A missing badge is not worth breaking the page over.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, auction, lot, myPosition.hasBid]);

  // The lot as it stands now: the engine's figures over the catalogue row.
  const liveView = lot !== null && feed !== null ? feed.lot(lot.id) : undefined;
  const youOnLot = lot !== null && feed !== null ? feed.you(lot.id) : undefined;
  const view = lot === null ? null : withLiveFigures(lot, liveView);
  const liveStatus = view?.status;

  // Mirror the place_bid gate exactly: the auction must be 'active' or 'preview';
  // an 'active' lot is biddable; a 'scheduled' lot is biddable only when the
  // auction opts into preview bidding (so a preview auction is browse-only unless
  // the flag is on). UI feedback only — place_bid remains the security boundary.
  // The status is the LIVE one (the engine's, on a sale it runs).
  const isBiddable = useMemo(() => {
    if (auction?.status !== 'active' && auction?.status !== 'preview') return false;
    if (liveStatus === 'active') return true;
    if (liveStatus === 'scheduled' && auction.allow_preview_bidding) return true;
    return false;
  }, [auction, liveStatus]);

  const needsApproval = useMemo(() => {
    return Boolean(auction?.requires_bidder_approval) && approvalStatus !== 'approved';
  }, [auction, approvalStatus]);

  const needsPreauth = useMemo(() => {
    return (
      Boolean(auction?.requires_cc_preauth) &&
      (!auction?.requires_bidder_approval || approvalStatus === 'approved') &&
      !hasActivePreauth
    );
  }, [auction, approvalStatus, hasActivePreauth]);

  /**
   * The next minimum: the ENGINE's (`next_min_cents`, its own increment rules)
   * on a sale it runs, once it has described the lot — never re-derived here;
   * the scale lookup (`computeNextMin`) otherwise.
   */
  const effectiveNextMin = liveView?.next_min_cents ?? nextMinCents;

  /**
   * The four quick amounts, from the phone's function.
   *
   * They set a MAXIMUM, not the amount that gets posted, so they are offsets
   * from the minimum rather than ladder steps — a ceiling does not have to land
   * on an increment boundary. See `bidIncrementTiles`.
   */
  const tiles = useMemo(
    () => (effectiveNextMin === null ? [] : bidIncrementTiles(effectiveNextMin, formatCents)),
    [effectiveNextMin],
  );

  /** The cents the CTA would send — a chosen tile, or whatever was typed. */
  const pickedCents = useMemo(() => {
    if (bidAmount.trim() === '') return effectiveNextMin;
    return parseBidToCents(bidAmount);
  }, [bidAmount, effectiveNextMin]);

  /** What the bidder holds: the engine's ceiling over the database copy. */
  const position = positionWithYou(myPosition, youOnLot);

  /**
   * Watching a lot — now via `@auctisync/api-client` so the website and the
   * phone share ONE implementation (Run Brief P2.7). The previous version
   * inlined an insert here, and a plain insert on the `(user_id, lot_id)`
   * primary key raises 23505 for a lot that is ALREADY watched — which this
   * treated as failure and left the heart un-filled on a lot that is on the
   * list. `setWatched` upserts, so the operation converges on the state that
   * was asked for.
   */
  async function handleToggleWatch() {
    if (!session || !lot) {
      void navigate('/signin', { state: { from: location } });
      return;
    }
    const next = !isWatched;
    setWatchBusy(true);
    const { error: err } = await setWatched(supabase, session.user.id, lot.id, next);
    setWatchBusy(false);
    if (err === undefined) setIsWatched(next);
  }

  async function handleRequestApproval() {
    if (!session || !auction) {
      void navigate('/signin', { state: { from: location } });
      return;
    }
    setApprovalBusy(true);
    setApprovalMsg(null);
    const { error: err } = await supabase.from('bidder_approvals').insert({
      tenant_id: auction.tenant_id,
      auction_id: auction.id,
      user_id: session.user.id,
      status: 'pending',
      request_note: approvalRequestNote.trim() || null,
    });
    setApprovalBusy(false);
    if (err) {
      setApprovalMsg(err.message);
    } else {
      setApprovalStatus('pending');
      setApprovalMsg('Request sent. The auction house will review it.');
      setApprovalRequestNote('');
    }
  }

  async function submitBid(enteredCents: number) {
    setBidNotice(null);
    setBidOk(null);
    setReject(null);

    if (!session) {
      void navigate('/signin', { state: { from: location } });
      return;
    }
    if (!lot || effectiveNextMin === null) return;

    if (!Number.isFinite(enteredCents) || enteredCents <= 0) {
      setBidNotice('Enter a whole amount in dollars and cents, like 1200 or 1200.50.');
      return;
    }
    // Caught before the RPC: place_bid ACCEPTS a ceiling at or below one the
    // bidder already holds, writes a ledger row, changes nothing and reports
    // success — which reads as the site being broken.
    const pointless = explainPointlessBid(position, enteredCents);
    if (pointless !== null) {
      setBidNotice(pointless.text);
      return;
    }
    if (enteredCents < effectiveNextMin) {
      setBidNotice(`The next bid must be at least ${formatCents(effectiveNextMin)}.`);
      return;
    }

    setBidBusy(true);
    // The Supabase book's own-bid test compares holdings against these. (On
    // the engine, "own" is the engine's `you`; this is unused there.)
    pendingOwnRef.current = positionRef.current;
    // The entered amount is the bidder's MAXIMUM (secret ceiling). The server
    // holds it and bids only as much as needed to lead; the stored floor is
    // derived. Sending amount == max keeps a plain next-min bid byte-identical.
    const res = await apiClient.placeBid({
      lotId: lot.id,
      amountCents: enteredCents,
      maxAmountCents: enteredCents,
      idempotencyKey: crypto.randomUUID(),
      source: 'web',
    });
    setBidBusy(false);

    // A transport failure comes back as { success:false, reason:'rpc_error',
    // message } rather than throwing, and `describeBidFailure` surfaces
    // `message` first. That mapping is SHARED with the native app so a bidder
    // is never told two different things about the same rejection.
    if (res.success) {
      // The primer has done its job the moment a bid actually lands — not when
      // one is attempted, or a rejected first bid would spend it.
      markFirstBid();
      // A bid watches the lot (trigger `tg_bids_add_to_watchlist`, 2026-09-08).
      // The server has already done it; this is the button catching up, so a
      // bidder does not look at an un-filled heart on a lot they just bid on and
      // tap it — which would UN-watch the thing the bid just watched.
      setIsWatched(true);
      void refreshMyPosition();
      void refreshHistory();
      setBidOk(
        res.triggered_extension
          ? `Bid placed. The timer extended. Current bid ${formatCents(res.current_bid_cents ?? enteredCents)}.`
          : `Bid placed. Current bid ${formatCents(res.current_bid_cents ?? enteredCents)}.`,
      );
      setBidAmount('');
      await refreshLot();
    } else {
      pendingOwnRef.current = null;
      const msg = describeBidFailure(res);
      // A NOTICE is not a refusal — it stays inline and calm. Only an error
      // takes the dialog, which is the web's answer to a deliberate click.
      if (msg.tone === 'notice') {
        setBidNotice(msg.text);
      } else {
        setReject({ text: msg.text, amountCents: enteredCents });
      }
      // The price is the news in a rejection, so re-read it before the dialog
      // quotes a number.
      await refreshLot();
    }
  }

  if (loading) {
    return (
      <div className="w-wrap" style={{ padding: '48px 0' }}>
        <p className="muted">Loading lot…</p>
      </div>
    );
  }
  if (error || !auction || !lot) {
    return (
      <div className="w-wrap" style={{ padding: '48px 0' }}>
        <div className="w-empty">
          <div className="w-empty-i" aria-hidden>
            ⌕
          </div>
          <h2 className="w-h2">{error ?? 'That lot is not here.'}</h2>
          <Link to="/auctions" className="w-btn w-btn-out">
            Back to the sales
          </Link>
        </div>
      </div>
    );
  }

  // Derived from the public-safe generated columns, never from reserve_cents:
  // anon cannot read the reserve VALUE (migration 20260720130000), only its
  // STATUS. `reserve_satisfied` tracks bidding live — unlike `reserve_met`,
  // which close_auctions() writes only at settlement.
  //
  // On the engine its own `reserve_met` decides, once it has described the lot.
  const reserveSatisfied = liveView?.reserve_met ?? lot.reserve_satisfied;
  const reserveState = !lot.has_reserve
    ? 'no_reserve'
    : reserveSatisfied
      ? 'reserve_met'
      : 'reserve_not_met';

  /**
   * The figures the panel prints — the shared `toLiveLot`, the same mapping
   * the app's lot screen uses: the engine's price, count and clock over the
   * row, a hidden figure flagged rather than shown, and the standing from the
   * engine's `you` once it has spoken (the RPC's until then).
   */
  const shown = toLiveLot(lot, {
    nowMs: nowServerMs(),
    live: liveView ?? null,
    you: youOnLot,
    standing: standing === 'winning' || standing === 'outbid' ? standing : null,
  });
  const lotNow = view ?? lot;

  const guest = session === null;
  const closed = !isBiddable;
  const premiumPct = auction.buyers_premium_bps === null ? null : auction.buyers_premium_bps / 100;
  // The house, not the sale. `activeHouse` is whichever the bidder chose, else
  // the one this page discovered.
  const houseName = activeHouse === null ? 'the auction house' : houseLabel(activeHouse);
  const title = lot.title ?? '(untitled)';

  return (
    <>
      <LiveStatusBanner status={live.status} timeZone={auction.timezone} />
      {/* 🔴 THE SAME HEADER THE LOTS PAGE CARRIES. Jim, 2026-09-18: *"The new
          header should be in place when the user opens a lot card too."* A
          bidder arriving straight on a lot — from a link, a search result, an
          email — had no idea which sale they were in beyond a breadcrumb, and
          none of the terms they are about to bid under.

          Outside `.w-wrap` for the same reason it is on the Lots page: the wrap
          caps at `--maxw`, so a band inside it cannot reach the edges.

          ⚠️ NO `lotEndsAt`. On the Lots page that array is every DISPLAYED lot's
          close, and the earliest of them is when the sale starts closing. Here
          there is exactly one lot, and passing its close would state that THIS
          lot's time is when the whole sale begins closing — true only by
          accident, and only for the first lot. Left empty, `saleClosingBeginsAt`
          falls back to the sale's own `ends_at` and shows nothing when the house
          never set one, which is the honest answer. */}
      <section className="lr-topband">
        <div className="lr-top">
          <SaleHeader sale={auction} houseName={houseName} nowServerMs={nowServerMs} />
          {/* 🔴 THE SAME TWO PANELS THE LOTS PAGE CARRIES. Jim, 2026-09-18: *"I
              want both of those tables in the headers on Home, Lot, and
              Watchlist on the website."* A bidder looking at one lot still wants
              to know they are winning three others in the same sale — that is
              the fact that decides whether they push on this one. */}
          <SaleAside
            standing={saleStanding}
            events={saleEvents}
            offline={connection === 'offline'}
            nowServerMs={nowServerMs}
          />
        </div>
      </section>

      <div className="w-wrap">
        <div className="w-crumbs">
          <Link to="/">Home</Link>
          <span aria-hidden>›</span>
          <Link to={`/auctions/${slug ?? ''}`}>{auction.title}</Link>
          <span aria-hidden>›</span>
          <span style={{ color: 'var(--text)' }}>Lot {lot.lot_number}</span>
          {/* Module #23 chunk 2a — follow this auction's seller (tenant). It
              has no home in the design's panel, which is reserved for the act
              of bidding; the crumb row is where "who is this" already lives. */}
          <span style={{ marginLeft: 'auto' }}>
            <FollowButton kind="tenant" targetId={auction.tenant_id} />
          </span>
        </div>

        <div className="w-lot">
          <SwipePhotoCarousel
            photos={photos}
            alt={title}
            {...(reserveState === 'no_reserve'
              ? { badge: <span className="tag t-nr">No reserve</span> }
              : {})}
          />

          <div className="w-lot-body">
            {lot.description ? (
              <section className="w-sec" style={{ paddingTop: 8 }}>
                <h2 className="w-h2" style={{ fontSize: 20, marginBottom: 14 }}>
                  About this lot
                </h2>
                <p className="w-body" style={{ whiteSpace: 'pre-wrap' }}>
                  {lot.description}
                </p>
              </section>
            ) : null}

            {conditionLabel(lot.condition) ? (
              <section className="w-sec" style={{ paddingTop: lot.description ? 30 : 8 }}>
                <h2 className="w-h2" style={{ fontSize: 20, marginBottom: 14 }}>
                  Condition
                </h2>
                {/* The label, not the code — `conditionLabel`, shared with the app. */}
                <p className="w-body">{conditionLabel(lot.condition)}</p>
              </section>
            ) : null}

            {/* The prices the house ticked "Show on listing" for (Jim,
                2026-10-06) — MSRP by default; retail and the estimate when the
                house chooses. `listingPriceLines` decides, the same rule the
                app uses, and reads only the bidder copies the server fills
                when a box is ticked. */}
            {listingPriceLines(lot).map((p) => (
              <section
                key={p.key}
                className="w-sec"
                style={{ paddingTop: 30 }}
                data-testid={`lot-price-${p.key}`}
              >
                <h2 className="w-h2" style={{ fontSize: 20, marginBottom: 14 }}>
                  {p.label}
                </h2>
                <p className="w-body">{p.value}</p>
              </section>
            ))}

            {/* The four numbers a bidder deciding what to pay actually asks
                for. `Opened at` rather than a "% above start" line: the raw
                pair is the honest form, and the reader can do the subtraction
                they care about. */}
            <div className="w-fact" style={{ marginTop: 22 }}>
              <div>
                <span className="w-lbl">Bids</span>
                <b>{shown.bidsHidden ? '—' : shown.bids}</b>
              </div>
              <div>
                <span className="w-lbl">Watching</span>
                <b>{lot.watch_count}</b>
              </div>
              <div>
                <span className="w-lbl">Views</span>
                <b>{lot.view_count}</b>
              </div>
              <div>
                <span className="w-lbl">Opened at</span>
                <b>{formatCents(lot.starting_bid_cents)}</b>
              </div>
            </div>

            {/* Module #23 chunk 5 — the same function the phone calls, but fed
                ONLY the inputs the strip above does not already draw. Watch and
                view counts are in the strip; repeating them as a sentence
                underneath is the page saying the same number twice. Each rule
                degrades to "absent" on a missing input, by the component's own
                design. */}
            {/* A figure the house hides is not the facts' to state either. */}
            {shown.bidsHidden || shown.priceHidden ? null : (
              <LotFunFacts
                bidCount={shown.bids}
                currentBidCents={lotNow.current_bid_cents}
                startingBidCents={lot.starting_bid_cents}
                quantity={lot.quantity}
              />
            )}

            {specs ? <SpecsTable specs={specs} /> : null}

            <section className="w-sec" style={{ paddingTop: 34 }}>
              <details className="w-acc" open>
                <summary className="w-acc-h">
                  Pickup and payment<span aria-hidden>›</span>
                </summary>
                <div className="w-acc-b">
                  {pickup?.window ? (
                    <div className="w-kv">
                      <span className="muted">Pickup</span>
                      <span>{pickup.window}</span>
                    </div>
                  ) : null}
                  {(pickup?.address ?? pickup?.location) ? (
                    <div className="w-kv">
                      <span className="muted">Where</span>
                      <span>{[pickup.location, pickup.address].filter(Boolean).join(' · ')}</span>
                    </div>
                  ) : null}
                  {premiumPct !== null ? (
                    <div className="w-kv">
                      <span className="muted">Buyer&apos;s premium</span>
                      <span>{premiumPct}%</span>
                    </div>
                  ) : null}
                  <div className="w-kv">
                    <span className="muted">Payment</span>
                    <span>
                      {auction.requires_cc_preauth
                        ? 'Card on file, charged after the sale'
                        : 'Invoiced after the sale'}
                    </span>
                  </div>
                  {/* Phase 1 is LOCAL auctions — collection in person, never
                      shipping. Saying so here is cheaper than a support email
                      after the hammer. */}
                  <div className="w-kv">
                    <span className="muted">Shipping</span>
                    <span>Not offered — collection only</span>
                  </div>
                  {pickup?.instructions ? (
                    <p className="w-body muted" style={{ fontSize: 13.5, marginTop: 12 }}>
                      {pickup.instructions}
                    </p>
                  ) : null}
                  {pickup !== null && pickup.from === 'none' ? (
                    <p className="w-body muted" style={{ fontSize: 13.5, marginTop: 12 }}>
                      Collection details for this sale have not been published yet.
                    </p>
                  ) : null}
                </div>
              </details>
            </section>

            {/* The section is no longer removed outright when the house hides the
                trail. `get_lot_bid_history` now returns only the caller's OWN
                bids in that case (20260908130000), so what is left is theirs and
                worth showing — and a bidder who is told nothing cannot tell
                "no bids" from "not published". The server decides what arrives;
                this only decides how to say why it is short. */}
            {auction.hide_bid_history && bidHistory.length === 0 ? (
              <section className="w-sec" style={{ paddingTop: 30 }}>
                <h2 className="w-h2" style={{ fontSize: 20, marginBottom: 10 }}>
                  Bid history
                </h2>
                <p className="muted" style={{ fontSize: 13 }}>
                  {houseName} does not publish the bid-by-bid trail for this sale. The bid count
                  above is live.
                </p>
              </section>
            ) : (
              <section className="w-sec" style={{ paddingTop: 30 }}>
                <div className="w-sech">
                  <h2 className="w-h2" style={{ fontSize: 20 }}>
                    Bid history
                    {bidHistory.length > 0
                      ? ` · ${String(bidHistory.length)} bid${bidHistory.length === 1 ? '' : 's'}`
                      : ''}
                  </h2>
                  <SoundControls />
                </div>
                {bidHistory.length === 0 ? (
                  <p className="muted" style={{ fontSize: 14 }}>
                    No bids on this lot yet.
                  </p>
                ) : (
                  <>
                    <div
                      style={{
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--r-card)',
                        padding: '4px 18px',
                      }}
                    >
                      {bidHistory.map((h) => {
                        const you = bidHistoryLabel({ isYou: isOwnEntry(h, ownSeqs) }) === 'you';
                        return (
                          <div className="w-kv" key={h.id}>
                            <span style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                              <span
                                style={{
                                  fontWeight: you ? 700 : 500,
                                  color: you ? 'var(--text)' : 'var(--text-muted)',
                                }}
                              >
                                {you ? 'You' : 'bidder'}
                              </span>
                              <span className="muted mono" style={{ fontSize: 12 }}>
                                {bidAgeLabel(h.createdAt, nowServerMs())}
                              </span>
                              {h.isProxy ? (
                                <span className="muted" style={{ fontSize: 12 }}>
                                  automatic
                                </span>
                              ) : null}
                            </span>
                            {/* NULL means "not yours to see": the house hides
                                winning info and this is somebody else's bid. It
                                is never null for the caller's own. */}
                            {h.amountCents === null ? (
                              <span className="muted mono" style={{ fontWeight: 600 }}>
                                —
                              </span>
                            ) : (
                              <span className="mono" style={{ fontWeight: 600 }}>
                                {formatCents(h.amountCents)}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <p className="muted" style={{ fontSize: 12.5, marginTop: 10 }}>
                      {auction.hide_bid_history
                        ? `${houseName} does not publish the bid-by-bid trail for this sale. Your own bids are always shown to you.`
                        : auction.hide_winning_info
                          ? `Every other bidder shows as “bidder”, and ${houseName} does not publish their amounts. Maximums are never shown to anyone.`
                          : 'Every other bidder shows as “bidder”. Maximums are never shown to anyone.'}
                    </p>
                  </>
                )}
              </section>
            )}

            {/* Module #23 chunk 8 — bidder-set smart alerts on this lot. Anchor
                id: the panel's Alerts button jumps here rather than opening a
                second control for the same thing. */}
            <section className="w-sec" style={{ paddingTop: 30 }} id="lot-alerts">
              <h2 className="w-h2" style={{ fontSize: 20, marginBottom: 14 }}>
                Alerts
              </h2>
              <LotAlertControls
                lotId={lot.id}
                hasReserve={lot.has_reserve ?? false}
                reserveMet={liveView?.reserve_met ?? lot.reserve_met}
              />
            </section>

            {/* Module #23 chunk 4 — Q&A. */}
            <section className="w-sec" style={{ paddingTop: 30 }}>
              <LotQA lotId={lot.id} tenantId={auction.tenant_id} />
            </section>

            <div
              className="w-sec"
              style={{ paddingTop: 26, display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}
            >
              {/* IN-SITE now that `/help` exists. These used to leave for
                  help.auctisync.com mid-bid, which on a house build is the site
                  handing a bidder to somebody else's domain. */}
              <Link to="/help/placing-your-first-bid">How to bid</Link>
              <Link to="/help/anti-snipe-for-bidders">Why timers extend</Link>
              <Link to="/help/buyer-premium-explained">Buyer&apos;s premium</Link>
            </div>
          </div>

          {/* ── the panel ── */}
          <aside className="w-panel">
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 7,
                  flexWrap: 'wrap',
                }}
              >
                <span className="w-lbl">Lot {lot.lot_number}</span>
                {shown.you === 'winning' ? <span className="tag t-win">Winning</span> : null}
                {shown.you === 'outbid' ? <span className="tag t-out">Outbid</span> : null}
                {reserveState === 'reserve_met' ? (
                  <span className="tag t-win">Reserve met</span>
                ) : null}
                {(lotNow.status === 'active' || lotNow.status === 'closing') && (
                  <UrgencyBadge endsAt={lotNow.ends_at} nowServerMs={nowServerMs} />
                )}
                <LotBadges
                  bidCount={shown.bidsHidden ? null : shown.bids}
                  reserveMet={liveView?.reserve_met ?? lot.reserve_met}
                  quantity={lot.quantity}
                  createdAt={lot.created_at}
                  exclude={['reserve_met']}
                />
              </div>
              <h1 className="w-h3" style={{ fontSize: 17, lineHeight: 1.35 }}>
                {title}
              </h1>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div>
                <div className="w-lbl">
                  {lotNow.current_bid_cents !== null || shown.priceHidden
                    ? 'Current bid'
                    : 'Starting bid'}
                </div>
                <div className="w-price" style={{ fontSize: 38, lineHeight: 1.05, marginTop: 2 }}>
                  {/* A price the house hides from this viewer is a dash, never the opening bid. */}
                  {shown.priceHidden ? '—' : formatCents(shown.current)}
                </div>
                <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>
                  {shown.bidsHidden
                    ? ''
                    : `${String(shown.bids)} bid${shown.bids === 1 ? '' : 's'}`}
                  {effectiveNextMin !== null && !closed
                    ? `${shown.bidsHidden ? '' : ' · '}next min ${formatCents(effectiveNextMin)}`
                    : ''}
                  {isLive ? (
                    <>
                      {' · '}
                      <span
                        title="Bids and the timer update live"
                        style={{ color: 'var(--semantic-winning)' }}
                      >
                        live
                      </span>
                    </>
                  ) : null}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div className="w-lbl">{closed ? 'Closed' : 'Closes in'}</div>
                <div style={{ marginTop: 4 }}>
                  <Countdown endsAt={lotNow.ends_at} nowServerMs={nowServerMs} className="cd" />
                </div>
              </div>
            </div>

            {session ? (
              <div className="muted" style={{ fontSize: 13 }} data-testid="my-lot-position">
                {describeMyPosition(position, lotNow.current_bid_cents)}
              </div>
            ) : null}

            {bidOk ? (
              <div
                role="status"
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                  padding: '12px 14px',
                  borderRadius: 'var(--r-control)',
                  background:
                    'color-mix(in oklab, var(--semantic-winning) 12%, var(--surface-elev))',
                  border: '1px solid color-mix(in oklab, var(--semantic-winning) 34%, transparent)',
                }}
              >
                <span style={{ color: 'var(--semantic-winning)' }} aria-hidden>
                  ✓
                </span>
                <span style={{ fontSize: 13.5, fontWeight: 600 }}>{bidOk}</span>
              </div>
            ) : null}

            <hr className="w-rule" />

            {guest ? (
              <>
                <Link
                  to="/signin"
                  state={{ from: location }}
                  className="w-btn w-btn-cta w-btn-lg w-btn-full"
                >
                  Sign in to bid
                </Link>
                {/* Watching a lot needs an account too — a watchlist belongs to
                    somebody, and there is nothing for a signed-out visitor to
                    have a list of. Jim, 2026-09-03. */}
                <p className="muted" style={{ fontSize: 12.5, margin: 0, lineHeight: 1.5 }}>
                  Bidding and watching both need an account with {houseName}. Browsing does not.
                </p>
              </>
            ) : closed ? (
              <div
                style={{
                  padding: '14px 16px',
                  borderRadius: 'var(--r-control)',
                  background: 'var(--surface-muted)',
                  border: '1px solid var(--border)',
                }}
              >
                <b style={{ fontFamily: 'var(--font-heading)', fontSize: 14.5 }}>
                  Bidding is closed on this lot
                </b>
                <p className="muted" style={{ fontSize: 13, margin: '6px 0 0', lineHeight: 1.5 }}>
                  {auction.status === 'active'
                    ? `This lot is ${lotNow.status}.`
                    : `The sale is ${auction.status}.`}
                </p>
              </div>
            ) : needsApproval ? (
              <ApprovalPanel
                status={approvalStatus}
                decisionNote={approvalDecisionNote}
                note={approvalRequestNote}
                onNoteChange={setApprovalRequestNote}
                onRequest={() => {
                  void handleRequestApproval();
                }}
                busy={approvalBusy}
                message={approvalMsg}
                houseName={houseName}
              />
            ) : needsPreauth ? (
              <CcPreauthPanel
                key={preauthRefreshKey}
                auctionId={auction.id}
                userId={session.user.id}
                onAuthorized={() => {
                  setHasActivePreauth(true);
                  setPreauthRefreshKey((k) => k + 1);
                }}
              />
            ) : (
              <>
                <div>
                  <div
                    className="w-lbl"
                    style={{
                      marginBottom: 9,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    Bid amount
                    <HelpTooltip helpSlug="how-maximum-bids-work">
                      Enter the most you&apos;re willing to pay — your maximum. We bid up to it for
                      you, only as much as needed to stay in front, and never reveal it to anyone.
                    </HelpTooltip>
                  </div>
                  <div className="w-inc">
                    {tiles.map((tile) => {
                      const dollars = (tile.cents / 100).toFixed(2);
                      return (
                        <button
                          key={tile.cents}
                          type="button"
                          className="w-inct"
                          aria-pressed={pickedCents === tile.cents}
                          onClick={() => setBidAmount(dollars)}
                        >
                          <b>{formatCents(tile.cents)}</b>
                          <i>{tile.label === 'Minimum' ? 'min' : tile.label}</i>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <label style={{ display: 'grid', gap: 6 }}>
                  <span className="w-lbl">Or type your own maximum</span>
                  <span style={{ position: 'relative', display: 'block' }}>
                    <span
                      aria-hidden
                      style={{
                        position: 'absolute',
                        left: 12,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      $
                    </span>
                    <input
                      ref={bidField}
                      type="text"
                      inputMode="decimal"
                      value={bidAmount}
                      onChange={(e) => setBidAmount(e.target.value)}
                      placeholder={
                        effectiveNextMin === null ? '0.00' : (effectiveNextMin / 100).toFixed(2)
                      }
                      disabled={bidBusy}
                      style={{
                        width: '100%',
                        minHeight: 48,
                        padding: '0 14px 0 26px',
                        border: '1.5px solid var(--border-strong)',
                        borderRadius: 'var(--r-control)',
                        background: 'var(--surface)',
                        color: 'var(--text)',
                        fontFamily: 'var(--font-heading)',
                        fontSize: 16,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    />
                  </span>
                </label>

                <button
                  type="button"
                  className="w-btn w-btn-cta w-btn-lg w-btn-full"
                  disabled={bidBusy || pickedCents === null}
                  onClick={() => {
                    if (pickedCents !== null) void submitBid(pickedCents);
                  }}
                >
                  {bidBusy
                    ? 'Placing bid…'
                    : pickedCents === null
                      ? 'Enter an amount'
                      : `Bid ${formatCents(pickedCents)}`}
                </button>

                {bidNotice ? (
                  <div
                    role="status"
                    style={{
                      padding: '11px 13px',
                      borderRadius: 'var(--r-control)',
                      background: 'var(--surface-muted)',
                      border: '1px solid var(--border)',
                      color: 'var(--text-muted)',
                      fontSize: 13,
                      lineHeight: 1.5,
                    }}
                  >
                    {bidNotice}
                  </div>
                ) : showPrimer && pickedCents !== null ? (
                  /* THE FIRST-BID PRIMER — the design's, and the app's, at the
                     moment it is definitely being read.

                     "Proxy bidding" means nothing to a first-time bidder, and a
                     maximum is the single most misunderstood thing in an online
                     auction: people believe entering £50 spends £50. The lesson
                     also sits on Home as one of the three how-it-works facts,
                     but Home is read by someone browsing — this is read by
                     someone about to commit money.

                     It needs a priced amount to teach with, hence
                     `pickedCents !== null`: "X is your limit" with no X is
                     weaker than the plain sentence it would replace. */
                  <div
                    style={{
                      display: 'flex',
                      gap: 9,
                      alignItems: 'flex-start',
                      padding: '11px 13px',
                      borderRadius: 'var(--r-control)',
                      background: 'var(--surface-muted)',
                      border: '1px solid var(--border)',
                      fontSize: 12.5,
                      lineHeight: 1.5,
                      color: 'var(--text-muted)',
                    }}
                  >
                    <span aria-hidden>ⓘ</span>
                    <span>
                      Your first bid. {formatCents(pickedCents)} is your{' '}
                      <b style={{ color: 'var(--text)' }}>limit</b>, not what you pay — we bid up
                      only as far as we must, and stop if someone goes past it.
                    </span>
                  </div>
                ) : (
                  <p className="muted" style={{ fontSize: 12.5, margin: 0, lineHeight: 1.5 }}>
                    We bid only as much as needed to keep you in front, up to your maximum. Nobody
                    sees it — not the house, not other bidders.
                  </p>
                )}
              </>
            )}

            <hr className="w-rule" />
            <div style={{ display: 'flex', gap: 9 }}>
              <button
                type="button"
                className="w-btn w-btn-out w-btn-sm"
                style={{ flex: 1 }}
                disabled={watchBusy}
                aria-pressed={isWatched}
                onClick={() => {
                  void handleToggleWatch();
                }}
              >
                {isWatched ? '♥ Watching' : '♡ Watch'}
              </button>
              <a href="#lot-alerts" className="w-btn w-btn-out w-btn-sm" style={{ flex: 1 }}>
                Alerts
              </a>
              <button
                type="button"
                className="w-btn w-btn-out w-btn-sm"
                aria-label="Share this lot"
                onClick={() => {
                  void shareLot(title);
                }}
              >
                ↗
              </button>
            </div>
          </aside>
        </div>
      </div>

      {reject !== null ? (
        <BidRejectedDialog
          text={reject.text}
          enteredCents={reject.amountCents}
          currentBidCents={shown.priceHidden ? null : shown.current}
          nextMinCents={effectiveNextMin}
          busy={bidBusy}
          onClose={() => setReject(null)}
          onRebid={(cents) => {
            void submitBid(cents);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * A refused bid.
 *
 * 🔴 THE QUOTED PRICE IS THE ONE THAT CAUSED THE REFUSAL, never the value the
 * page was showing when the button was pressed — the rejection IS the news that
 * the price moved, and quoting the stale figure makes the dialog argue with
 * itself. `refreshLot` runs before this opens, so `currentBidCents` is current.
 *
 * The sentence comes from `describeBidFailure`, shared with the phone's sheet,
 * so a bidder is never told two different things about the same rejection. Only
 * the container differs: a centred dialog is the web's answer to a deliberate
 * click, where the phone used a bottom sheet.
 */
function BidRejectedDialog({
  text,
  enteredCents,
  currentBidCents,
  nextMinCents,
  busy,
  onClose,
  onRebid,
}: {
  text: string;
  enteredCents: number;
  /** Null when the house hides the price from this bidder: a dash. */
  currentBidCents: number | null;
  nextMinCents: number | null;
  busy: boolean;
  onClose: () => void;
  onRebid: (cents: number) => void;
}) {
  return (
    <div className="w-modal-wrap" role="dialog" aria-modal="true" aria-label="Bid not accepted">
      <div className="w-modal-bg" onClick={onClose} />
      <div className="w-modal">
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <div className="w-rj" data-tone="out" aria-hidden>
            !
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="w-h2" style={{ fontSize: 20 }}>
              Your bid was not accepted
            </h2>
            <p className="w-body" style={{ marginTop: 7, fontSize: 14.5 }}>
              {text}
            </p>
          </div>
          <button type="button" className="w-ico" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-control)',
            padding: '2px 15px',
          }}
        >
          <div className="w-kv">
            <span className="muted">You entered</span>
            <span className="mono" style={{ opacity: 0.55, textDecoration: 'line-through' }}>
              {formatCents(enteredCents)}
            </span>
          </div>
          <div className="w-kv">
            <span>Current bid</span>
            <span className="mono" style={{ fontWeight: 700 }}>
              {currentBidCents === null ? '—' : formatCents(currentBidCents)}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button type="button" className="w-btn w-btn-out" onClick={onClose}>
            Leave it
          </button>
          {nextMinCents !== null ? (
            <button
              type="button"
              className="w-btn w-btn-cta"
              disabled={busy}
              onClick={() => onRebid(nextMinCents)}
            >
              Bid {formatCents(nextMinCents)}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Share the lot. Native sheet where the browser has one, clipboard otherwise. */
async function shareLot(title: string) {
  const url = window.location.href;
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ title, url });
      return;
    } catch {
      // Cancelled, or refused by the browser. Fall through to the clipboard.
    }
  }
  try {
    await navigator.clipboard.writeText(url);
  } catch {
    // Nothing useful left to try; the address bar still has the link.
  }
}

/*
 * The bid-sound control moved to `components/bidder-web/SoundControls.tsx`
 * (2026-10-08): the Lots page carries the same control, and two copies writing
 * the same two settings would drift. It still sits beside the bid history here.
 */

function ApprovalPanel({
  status,
  decisionNote,
  note,
  onNoteChange,
  onRequest,
  busy,
  message,
  houseName,
}: {
  status: 'none' | 'pending' | 'approved' | 'rejected' | 'revoked';
  decisionNote: string | null;
  note: string;
  onNoteChange: (v: string) => void;
  onRequest: () => void;
  busy: boolean;
  message: string | null;
  houseName: string;
}) {
  return (
    <div
      style={{
        padding: '14px 16px',
        borderRadius: 'var(--r-control)',
        background: 'var(--brand-wash-strong)',
        border: '1px solid var(--brand-hairline)',
        display: 'grid',
        gap: 10,
      }}
    >
      <b style={{ fontFamily: 'var(--font-heading)', fontSize: 14.5 }}>
        {status === 'pending'
          ? `${houseName} is reviewing your registration`
          : status === 'rejected' || status === 'revoked'
            ? `${houseName} has not approved you for this sale`
            : `${houseName} approves its own bidders`}
      </b>
      <p className="muted" style={{ fontSize: 13, margin: 0, lineHeight: 1.5 }}>
        {status === 'none' &&
          'Send a request and they will review it. You can watch lots while you wait.'}
        {status === 'pending' &&
          'They approve their own bidders, usually within a few hours. You can watch lots while you wait.'}
        {(status === 'rejected' || status === 'revoked') &&
          (decisionNote
            ? `Note from the house: “${decisionNote}”`
            : 'Contact the house if you think this is a mistake.')}
      </p>
      {status === 'none' && (
        <>
          <textarea
            value={note}
            onChange={(e) => onNoteChange(e.target.value)}
            rows={2}
            placeholder="Optional note to the auction house…"
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-control)',
              background: 'var(--surface)',
              color: 'var(--text)',
              fontFamily: 'inherit',
              fontSize: 14,
              resize: 'vertical',
            }}
          />
          <button
            type="button"
            className="w-btn w-btn-cta w-btn-full"
            disabled={busy}
            onClick={onRequest}
          >
            {busy ? 'Sending…' : 'Request approval to bid'}
          </button>
        </>
      )}
      {message && (
        <div role="status" className="muted" style={{ fontSize: 13 }}>
          {message}
        </div>
      )}
    </div>
  );
}
